import { READING_SKILLS_MAP } from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.constants";

// The assignment page can also take a CSV list for `contentIds` representing bundle members
// but since it already fetching members on the page itself, passing in the contentId should suffice.
export const getAssignHref = (returnUrl: string, contentId: string) =>
  `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;

export const getReadingSkillsLabels = (
  readingSkills: string[] | null,
): string[] => {
  if (!readingSkills?.length) return [];

  return [
    ...new Set(
      readingSkills
        .map((key) => READING_SKILLS_MAP[key] as string)
        .filter((readingSkill) => !!readingSkill),
    ),
  ].slice(0, 2);
};

export const getFormattedGradeLevelLabel = (
  gradeLevels: string[] | null,
): string => {
  if (!gradeLevels || gradeLevels.length === 0) return "";

  const gradeMap = {
    K: "Kindergarten ",
    1: "1st",
    2: "2nd",
    3: "3rd",
    4: "4th",
    5: "5th",
    6: "6th",
    7: "7th",
    8: "8th",
    9: "9th",
    10: "10th",
    11: "11th",
    12: "12th",
  };

  const sortedGrades = gradeLevels
    .filter((grade) => !!grade)
    .sort((a, b) => {
      if (a === "Grade K") {
        return -1;
      } else if (b === "Grade K") {
        return 1;
      } else {
        const gradeA = a.split(" ")[1] || "";
        const gradeB = b.split(" ")[1] || "";
        return gradeA.localeCompare(gradeB, undefined, { numeric: true });
      }
    });

  const getGradeValue = (grade: string) =>
    grade === "Grade K" ? "K" : grade.split(" ")[1];
  const formatResult = (grade: keyof typeof gradeMap) =>
    gradeMap[grade] || `${grade}th`;
  const lowestGradeValue = getGradeValue(sortedGrades[0] || "");
  const highestGradeValue = getGradeValue(
    sortedGrades[sortedGrades.length - 1] || "",
  );
  const lowestGradeIsDifferentFromHighestGrade =
    lowestGradeValue !== highestGradeValue;
  if (lowestGradeIsDifferentFromHighestGrade) {
    return `${formatResult(lowestGradeValue as keyof typeof gradeMap)}-${formatResult(highestGradeValue as keyof typeof gradeMap)}`;
  } else {
    return formatResult(lowestGradeValue as keyof typeof gradeMap);
  }
};

export const getFormattedLexileLevelLabel = (
  lexileLevels: number[],
): string => {
  if (!lexileLevels?.length || !lexileLevels[0]) return "";

  const sortedLexiles = lexileLevels.sort((a, b) => a - b);

  if (lexileLevels?.length === 1) return `${sortedLexiles[0] as number}L`;

  const lowestLexile = sortedLexiles[0]?.toString() as string;
  const highestLexile = sortedLexiles[
    sortedLexiles.length - 1
  ]?.toString() as string;

  return lowestLexile && highestLexile
    ? `${lowestLexile}L-${highestLexile}L`
    : "";
};

export const canUserSeeReadingSkills = ({
  activeELALicense,
  isLiteUser,
}: {
  activeELALicense: boolean;
  isLiteUser: boolean;
}) => activeELALicense || isLiteUser;
